import React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { WithStyle, withStyles, FormView } from 'rewire-ui';
import Typography from '@material-ui/core/Typography';
import { Theme } from '@material-ui/core/styles';
import Routes from '../Routes';
import authService from '../services/AuthService';
import Dialog from '../ui/Dialog2';
import TitleWithFlowLogo from '../ui/TitleWithFlowLogo';
import loginModel from './LoginModel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Link } from '../utility/Link';
import Button from '@material-ui/core/Button';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import IconButton from '@material-ui/core/IconButton';
import { ArrowRightCircleOutline } from 'mdi-material-ui';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { Observe } from 'rewire-core';
import { useObservable } from '../ui/useObservable';

const styles = (theme: Theme) => ({
  root: {
    width: '100%',
    height: '100vh',
    backgroundColor: theme.palette.primary.main,
  },
  emailButtonContainer: {
    padding: 0,
    margin: 0,
    alignItems: 'center',
    display: 'flex',
  },
  emailIcon: {
    color: theme.palette.dialogPrimary.main,
  },
  spinning: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '6px 16px',
    '& > a': {
      color: theme.palette.dialogPrimary.main,
      '&:hover': {
        color: theme.palette.dialogPrimary.light,
      },
      // '&:first-child': {
      //   marginBottom: '15px',
      // },
    },
  },
});

interface ILoginProps {}

type LoginProps = WithStyle<ReturnType<typeof styles>, ILoginProps & RouteComponentProps<any>>;

const RenderDialogContents = (props: any) => {
  const { classes } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const state = useObservable({ showPassword: false, isLoading: false });
  function isDisabled() {
    return loginModel.form.field.email.error !== undefined || state.isLoading || !loginModel.form.field.email.value;
  }

  async function executeAction() {
    if (!ref.current || state.isLoading) return;
    try {
      state.isLoading = true;
      switch (loginModel.authenticationFlow) {
        case 'UNKNOWN':
          await loginModel.getAuthenticationFlowForEmail(ref.current);
          break;

        case 'USER_PASSWORD':
          await loginModel.login();
          break;

        default:
          break;
      }
    } finally {
      state.isLoading = false;
    }
  }

  function _handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      executeAction();
      e.preventDefault();
    }
  }

  function toggleShowPasword() {
    state.showPassword = !state.showPassword;
  }

  loginModel.form.field.email.endAdornment = () => (
    <Observe>
      {() => (
        <div className={classes.emailButtonContainer}>
          <MailOutlineIcon />
          {loginModel.authenticationFlow === 'UNKNOWN' ? (
            <IconButton
              edge="end"
              disabled={isDisabled()}
              className={classes.emailIcon}
              aria-label="start sign in"
              size="small"
              title={'Press enter to sign in?'}
              onClick={executeAction}
            >
              <ArrowRightCircleOutline />
            </IconButton>
          ) : null}
        </div>
      )}
    </Observe>
  );

  loginModel.form.field.password.endAdornment = () => (
    <Observe>
      {() => (
        <div className={classes.emailButtonContainer}>
          {!state.showPassword ? <VisibilityIcon onClick={toggleShowPasword} /> : <VisibilityOffIcon onClick={toggleShowPasword} />}
          {loginModel.authenticationFlow === 'USER_PASSWORD' && !state.isLoading ? (
            <IconButton
              edge="end"
              disabled={isDisabled()}
              className={classes.emailIcon}
              aria-label="complete sign in"
              size="small"
              title={'Press enter to sign in?'}
              onClick={executeAction}
            >
              <ArrowRightCircleOutline />
            </IconButton>
          ) : null}
        </div>
      )}
    </Observe>
  );

  return (
    <FormView form={loginModel.form} onSubmit={executeAction}>
      <div className="content">
        <loginModel.form.field.email.Editor className="span1" onKeyDown={_handleKeyDown} />
      </div>
      <div className="content" ref={ref}>
        <Observe>
          {() => <loginModel.form.field.password.Editor className="span1" type={state.showPassword ? 'text' : 'password'} />}
        </Observe>
      </div>
      <Observe>
        {() => {
          return loginModel.authenticationFlow === 'USER_PASSWORD' && !state.isLoading ? (
            <div className={classes.linkContainer}>
              <Link to={Routes.ForgotPassword}>
                <Typography variant="body2">Forgot your password?</Typography>
              </Link>
            </div>
          ) : (
            <div className={classes.linkContainer}>
              <Button
                href="#"
                disabled={isDisabled()}
                size="small"
                onClick={() => (loginModel.authenticationFlow = 'USER_PASSWORD')}
              >
                Use Username/Password Authentication
              </Button>
            </div>
          );
        }}
      </Observe>
    </FormView>
  );
};

function Login(props: LoginProps) {
  React.useEffect(() => {
    loginModel.reset();
    loginModel.open(); // if we get here we want the dialog open!!
    return () => {
      loginModel.reset();
    };
  }, []);

  const renderTitle = React.useCallback((_dialog) => {
    return <TitleWithFlowLogo beforeText="Sign in to" />;
  }, []);

  const { from } = (props.location.state as { from: string }) || { from: { pathname: Routes.Home } };
  const { classes } = props;
  return (
    <Observe>
      {() => {
        if (authService.isAuthenticated) {
          return <Redirect to={from} />;
        }
        if (authService.isLoading) {
          return (
            <div className={classes.spinning}>
              <CircularProgress />
            </div>
          );
        }
        return (
          <div className={classes.root}>
            <Dialog dialog={loginModel} hideBackdrop={true} disableTransition={true} title={renderTitle} maxWidth="xs">
              <RenderDialogContents classes={classes} />
            </Dialog>
          </div>
        );
      }}
    </Observe>
  );
}

export default withStyles(styles, Login);
